import React, { memo } from 'react';
import { Box, Avatar, Typography } from '@mui/material';
import { SmartToy as BotIcon, Person as PersonIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Message } from '../types/types';
import { MessageFormatter } from './MessageFormatter';

interface ChatMessageProps {
  message: Message;
}

const formatTime = (date: Date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const ChatMessage = memo<ChatMessageProps>(({ message }) => {
  const theme = useTheme();
  const isUser = message.sender === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
        mb: 1.5,
        px: 1,
      }}
    >
      {!isUser && (
        <Avatar
          sx={{
            backgroundColor: theme.palette.primary.main,
            width: 32,
            height: 32,
            flexShrink: 0,
          }}
        >
          <BotIcon sx={{ fontSize: 18 }} />
        </Avatar>
      )}
      
      <Box
        sx={{
          maxWidth: '75%',
          backgroundColor: isUser 
            ? theme.palette.primary.main 
            : theme.palette.background.paper,
          color: isUser 
            ? 'white' 
            : theme.palette.text.primary,
          borderRadius: 2,
          p: 1.5,
          position: 'relative',
          boxShadow: 1,
          border: isUser ? 'none' : `1px solid ${theme.palette.divider}`,
        }}
      >
        <MessageFormatter text={message.text} isUser={isUser} />
        
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.75,
            opacity: 0.7,
            fontSize: '0.7rem',
            textAlign: 'right',
          }}
        >
          {formatTime(message.timestamp)}
        </Typography>
      </Box>

      {isUser && (
        <Avatar
          sx={{
            backgroundColor: theme.palette.secondary.main,
            width: 32,
            height: 32,
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ fontSize: 18 }} />
        </Avatar>
      )}
    </Box>
  );
});

ChatMessage.displayName = 'ChatMessage';
